import type { CSSProperties } from 'react';
import { Fragment, useMemo } from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import {
  getMaxHeight,
  renderEmptyCells
} from '@/pages/Markets/MarketsComponents/shared/tableHelpers';
import {
  sectionHeaderRowSx,
  sectionNameCellSx,
  shortTitleCellSx,
  tableContainerSx,
  tableHeadRowSx,
  tableSx
} from '@/pages/Markets/MarketsComponents/shared/tableStyles';

import MultiGroupTableRow from './MultiGroupTableRow';

import type { Sx } from '@/types/helpers';
import type { MultiGroupTableSectionType, SymbolPropertyConfig } from '../types';

const expandHeaderCellSx: Sx = { p: 0, pl: 1, pr: 1, width: 0 };

function collectColumns(sections: MultiGroupTableSectionType[]): SymbolPropertyConfig[] {
  const seen = new Set<string>();
  const ordered: SymbolPropertyConfig[] = [];
  for (const section of sections) {
    for (const row of section.rows) {
      for (const property of row.symbolBProperties) {
        if (!seen.has(property.name)) {
          seen.add(property.name);
          ordered.push(property);
        }
      }
    }
  }
  return ordered;
}

const MultiGroupTableTab = ({
  sections,
  height,
  isLoading = false
}: {
  sections: MultiGroupTableSectionType[];
  height?: number;
  isLoading?: boolean;
}) => {
  const columns = useMemo(() => collectColumns(sections), [sections]);

  const trailingHeaderCells = 3 + columns.length;

  return (
    <TableContainer
      sx={tableContainerSx}
      style={{ maxHeight: getMaxHeight(height) } as CSSProperties}
    >
      <Table
        stickyHeader
        aria-label='multi group table'
        size='small'
        sx={tableSx}
      >
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            <TableCell sx={expandHeaderCellSx} />
            <TableCell sx={shortTitleCellSx}>Short Title</TableCell>
            <TableCell align='right'>Date</TableCell>
            <TableCell align='right'>Last</TableCell>
            <TableCell align='right'>Change</TableCell>
            {columns.map(column => (
              <TableCell
                key={column.name}
                align='right'
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map(({ name, rows }) => (
            <Fragment key={name}>
              {name && (
                <TableRow
                  className='markets-table-row'
                  sx={sectionHeaderRowSx}
                >
                  <TableCell sx={expandHeaderCellSx} />
                  <TableCell sx={sectionNameCellSx}>{name}</TableCell>
                  {renderEmptyCells(trailingHeaderCells)}
                </TableRow>
              )}
              {rows.map((row, index) => (
                <MultiGroupTableRow
                  key={row.shortTitle}
                  columns={columns}
                  index={index}
                  isLoading={isLoading}
                  row={row}
                />
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MultiGroupTableTab;
